<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Mythic+ Runs</p>
          <h2>Dungeon progression</h2>
          <p>Top keys, team rosters, affix context and seasonal Raider.io cutoffs in one view.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Expansion: <span>{{ expansionLabel }}</span></span>
        <span class="filter-pill">Season: <span>{{ selectedSeason?.short_name || 'Current' }}</span></span>
        <span class="filter-pill">Region: <span>{{ filters.region }}</span></span>
        <span class="filter-pill">Dungeon: <span>{{ selectedDungeonLabel }}</span></span>
        <span class="filter-pill">Affixes: <span>{{ affixesFilterLabel }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Reset filters" title="Reset filters" @click="resetFilters">
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button class="btn-icon-outline" type="button" aria-label="Refresh" title="Refresh" @click="loadData(true)">
          <i class="fa-solid fa-rotate"></i>
        </button>
        <a class="btn-icon-outline" :href="leaderboardUrl" target="_blank" rel="noreferrer" aria-label="Open leaderboard" title="Open leaderboard">
          <i class="fa-solid fa-arrow-up-right-from-square"></i>
        </a>
        <button class="btn-icon" type="button" aria-label="Filters" title="Filters" @click="openFilters">
          <i class="fa-solid fa-sliders"></i>
        </button>
      </div>
    </div>

    <div
      v-if="isFiltersOpen"
      class="app-modal-backdrop filter-drawer-backdrop"
      :class="{ 'is-closing': closingFilters }"
      @click.self="closeFilters"
    >
      <aside class="app-filter-drawer" role="dialog" aria-modal="true" aria-labelledby="mythic-filters-title">
        <header>
          <h2 id="mythic-filters-title">Mythic+ filters</h2>
          <p>Season, region, dungeon and affix filters.</p>
        </header>
        <section>
          <form class="form filters-grid" @submit.prevent="applyFilters">
            <div role="group" class="field filter-field">
              <label class="label">Expansion</label>
              <select v-model.number="draftFilters.expansionId" class="select">
                <option :value="11">Midnight</option>
                <option :value="10">The War Within</option>
                <option :value="9">Dragonflight</option>
                <option :value="8">Shadowlands</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Season</label>
              <select v-model="draftFilters.season" class="select">
                <option v-for="season in draftSeasonOptions" :key="season.slug" :value="season.slug">
                  {{ season.name }} · {{ season.short_name }}
                </option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Region</label>
              <select v-model="draftFilters.region" class="select">
                <option value="world">World</option>
                <option value="eu">EU</option>
                <option value="us">US</option>
                <option value="kr">KR</option>
                <option value="tw">TW</option>
                <option value="cn">CN</option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Dungeon</label>
              <select v-model="draftFilters.dungeon" class="select">
                <option value="all">All</option>
                <option v-for="dungeon in draftVisibleDungeons" :key="dungeon.slug" :value="dungeon.slug">
                  {{ dungeon.name }}
                </option>
              </select>
            </div>

            <div role="group" class="field filter-field">
              <label class="label">Affixes</label>
              <select v-model="draftFilters.affixes" class="select">
                <option value="all">All</option>
                <option value="current">Current week</option>
                <option value="tyrannical">Tyrannical</option>
                <option value="fortified">Fortified</option>
              </select>
            </div>

          </form>
        </section>
        <footer>
          <button class="btn-outline" type="button" @click="closeFilters">Cancel</button>
          <button class="btn" type="button" @click="applyFilters">Apply</button>
        </footer>
      </aside>
    </div>

    <div v-if="error" class="card"><section>{{ error }}</section></div>

    <!-- <div class="stats-grid">
      <article class="stat-card card"><section><span>Runs loaded</span><strong>{{ filteredRuns.length }}</strong></section></article>
      <article class="stat-card card"><section><span>Best level</span><strong>{{ topMythicLevel }}</strong></section></article>
      <article class="stat-card card"><section><span>Current affixes</span><strong><span class="affix-icon-list"><span v-for="affix in affixes" :key="affix.id" class="affix-icon" :title="affix.name"><img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy"></span></span></strong></section></article>
      <article class="stat-card card"><section><span>Cutoff</span><strong>{{ cutoffLabel }}</strong></section></article>
    </div> -->

    <article class="season-reference-panel">
      <section class="reference-stack compact-reference">
        <div class="cutoff-statistics">
          <div class="mini-list compact-cutoffs">
            <div v-for="item in animatedCutoffEntries" :key="item.label" class="mini-list-row">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
        </div>
      </section>
    </article>

    <div class="content-grid mythic-runs-layout">
      <section class="mode-switch-panel">
        <button
          type="button"
          :class="{ 'is-active': contentMode === 'stats' }"
          @click="contentMode = 'stats'"
        >
          <i class="fa-solid fa-chart-simple"></i>
          <span>
            <strong>Statistics</strong>
            <small>Dungeon, class and spec distribution</small>
          </span>
        </button>
        <button
          type="button"
          :class="{ 'is-active': contentMode === 'ladder' }"
          @click="contentMode = 'ladder'"
        >
          <i class="fa-solid fa-table-list"></i>
          <span>
            <strong>Ladder</strong>
            <small>Runs table and card list</small>
          </span>
        </button>
      </section>

      <article class="card">
        <header class="section-head">
          <div><p class="eyebrow">{{ contentMode === 'stats' ? 'Statistics' : 'Runs' }}</p><h3>{{ contentMode === 'stats' ? 'Ladder insights' : 'Top runs' }}</h3></div>
          <div class="runs-head-actions">
            <div v-if="contentMode === 'ladder'" class="view-switch" aria-label="Display mode">
              <button
                class="btn-icon-outline"
                :class="{ 'is-active': viewMode === 'grid' }"
                type="button"
                aria-label="Cards"
                @click="viewMode = 'grid'"
              >
                <i class="fa-solid fa-grip"></i>
              </button>
              <button
                class="btn-icon-outline"
                :class="{ 'is-active': viewMode === 'table' }"
                type="button"
                aria-label="Table"
                @click="viewMode = 'table'"
              >
                <i class="fa-solid fa-table-list"></i>
              </button>
            </div>
          </div>
        </header>

        <section v-if="contentMode === 'stats' && showInitialSkeletons" class="stats-chart-skeleton-stack">
          <section v-for="chart in 2" :key="`stats-chart-skeleton-${chart}`" class="dungeon-chart-panel stats-chart-skeleton">
            <header class="dungeon-chart-head">
              <div class="stats-skeleton-title">
                <span class="skeleton-line skeleton-line-short"></span>
                <span class="skeleton-line skeleton-line-medium"></span>
              </div>
              <span class="skeleton-line stats-skeleton-total"></span>
            </header>

            <div v-if="chart === 1" class="stats-dungeon-skeleton-grid">
              <div v-for="bar in 8" :key="`stats-dungeon-bar-${bar}`" class="stats-dungeon-skeleton-item">
                <span class="skeleton-line stats-skeleton-value"></span>
                <span class="skeleton-media stats-skeleton-bar" :style="{ height: `${40 + ((bar * 17) % 52)}%` }"></span>
                <span class="skeleton-line stats-skeleton-label"></span>
              </div>
            </div>

            <div v-else class="stats-class-skeleton-list">
              <div v-for="row in 6" :key="`stats-class-row-${row}`" class="stats-class-skeleton-row">
                <span class="skeleton-avatar"></span>
                <span class="skeleton-line skeleton-line-medium"></span>
                <span class="skeleton-line stats-skeleton-track"></span>
                <span class="skeleton-line skeleton-line-short"></span>
              </div>
            </div>
          </section>
        </section>

        <section v-if="contentMode === 'stats' && showDungeonChart" class="dungeon-chart-panel">
          <header class="dungeon-chart-head">
            <div>
              <p class="eyebrow">Level distribution</p>
              <h4>Timed +{{ selectedChartLevel }} keys by dungeon</h4>
            </div>
            <div class="dungeon-chart-actions">
              <span>{{ maxLevelTeamTotal }} teams</span>
              <div v-if="chartLevelOptions.length > 1" class="view-switch" aria-label="Dungeon chart level">
                <button
                  v-for="level in chartLevelOptions"
                  :key="level"
                  class="btn-sm-outline"
                  :class="{ 'is-active': selectedChartLevel === level }"
                  type="button"
                  :aria-label="`Show level ${level} distribution`"
                  @click="setChartLevel(level)"
                >
                  +{{ level }}
                </button>
              </div>
            </div>
          </header>

          <div class="dungeon-chart" :key="`chart-${selectedChartLevel}`" :style="{ '--chart-max-count': chartMaxCount }">
            <div
              v-for="item in maxLevelDungeonStats"
              :key="`${selectedChartLevel}-${item.slug}`"
              class="dungeon-chart-item"
              :title="`${item.name}: ${item.count} teams`"
            >
              <span class="dungeon-chart-bar-wrap">
                <span class="dungeon-chart-value" :style="{ bottom: chartValueBottom(item.count) }">{{ item.count }}</span>
                <span class="dungeon-chart-bar" :style="{ height: chartBarHeight(item.count) }"></span>
              </span>
              <span class="dungeon-chart-label">{{ shortDungeonName(item.name) }}</span>
            </div>
          </div>
        </section>

        <section v-if="contentMode === 'stats' && showClassChart" class="dungeon-chart-panel class-chart-panel">
          <header class="dungeon-chart-head">
            <div>
              <p class="eyebrow">Class distribution</p>
              <h4>Classes and specs in the ladder</h4>
            </div>
          </header>

          <div class="class-chart">
            <div
              v-for="(item, index) in classDistributionStats"
              :key="item.slug"
              class="class-chart-row"
              :style="{ '--class-color': item.color, '--class-share': `${item.width}%` }"
              :title="`${item.name}: ${item.count} players`"
            >
              <div class="class-chart-main">
                <img :src="classIcon(item.slug)" :alt="item.name" loading="lazy">
                <div>
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.count }} players · {{ item.percent }}%</span>
                </div>
              </div>
              <div class="class-chart-track">
                <span class="class-chart-bar"></span>
              </div>
              <div class="class-spec-list">
                <span v-for="spec in item.specs" :key="`${item.slug}-${spec.slug}`">
                  {{ spec.name }} <strong>{{ spec.count }}</strong>
                </span>
              </div>
              <span class="class-chart-rank" :class="rankTierClass(index)">{{ index + 1 }}</span>
            </div>
          </div>
        </section>

        <section v-if="contentMode === 'ladder' && showInitialSkeletons" class="skeleton-stack">
          <div v-for="slot in 3" :key="`run-skeleton-${slot}`" class="card skeleton-card">
            <div class="skeleton-media"></div>
            <div class="skeleton-line skeleton-line-medium"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-row">
              <span class="skeleton-avatar"></span>
              <span class="skeleton-line skeleton-line-short"></span>
            </div>
          </div>
        </section>

        <section v-if="contentMode === 'ladder' && !showInitialSkeletons && viewMode === 'grid'" class="run-grid run-card-grid">
          <article v-for="entry in filteredRuns" :key="entry.run.keystone_run_id" class="dungeon-run-card">
            <a class="dungeon-run-media" :href="runUrl(entry)" target="_blank" rel="noreferrer">
              <img
                v-if="dungeonImage(entry.run.dungeon)"
                :src="dungeonImage(entry.run.dungeon)"
                :alt="entry.run.dungeon.name"
                loading="lazy"
              >
              <span class="run-level">{{ entry.run.mythic_level }}</span>
            </a>

            <section class="dungeon-run-body">
              <span class="faction-watermark" :class="`is-${entry.run.faction || 'mixed'}`">
                <img :src="factionBanner(entry.run.faction)" :alt="entry.run.faction || 'mixed faction'" loading="lazy">
              </span>

              <h4 class="dungeon-run-title">
                <span>#{{ entry.rank }}</span>
                <a :href="runUrl(entry)" target="_blank" rel="noreferrer">{{ entry.run.dungeon.name }}</a>
              </h4>

              <div class="run-summary-grid">
                <div>
                  <p>Affixes</p>
                  <div class="affix-list compact">
                    <a
                      v-for="affix in entry.run.weekly_modifiers"
                      :key="affix.id"
                      :href="affixUrl(affix)"
                      :title="affix.name"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
                    </a>
                  </div>
                </div>
                <div>
                  <p>Time</p>
                  <strong>{{ duration(entry.run.clear_time_ms) }} <span>({{ remainingTime(entry.run) }})</span></strong>
                </div>
                <div>
                  <p>Score</p>
                  <strong>{{ scoreValue(entry.score) }}</strong>
                </div>
              </div>

              <div class="run-team-block">
                <p>Team</p>
                <div class="run-team-grid">
                  <div v-for="member in entry.run.roster" :key="member.character.path" class="run-player-pill">
                    <span class="role-mark" :class="`is-${member.role}`">
                      <img :src="roleImage(member.role)" :alt="member.role" loading="lazy">
                    </span>
                    <a
                      class="player-name"
                      :style="{ color: classColor(member.character.class.slug) }"
                      :href="characterUrl(member.character.path)"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="player-actions">
                      <button
                        type="button"
                        :class="{ 'is-active': isFavoriteMember(member.character) }"
                        :aria-label="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        :title="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        @click="favoriteMember(member.character)"
                      >
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button
                        type="button"
                        :class="{ 'is-active': isCompareMember(member.character) }"
                        :aria-label="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        :title="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        @click="compareMember(member.character)"
                      >
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </section>

        <section v-if="false" class="run-grid run-card-grid">
          <div v-for="entry in filteredRuns" :key="entry.run.keystone_run_id" class="card run-card media-card">
            <section v-if="dungeonImage(entry.run.dungeon)" class="media-section">
              <img
                class="media-image"
                :src="dungeonImage(entry.run.dungeon)"
                :alt="entry.run.dungeon.name"
                loading="lazy"
              >
            </section>
            <header class="run-card-header">
              <div>
                <h4>#{{ entry.rank }} · {{ entry.run.dungeon.name }}</h4>
                <p>+{{ entry.run.mythic_level }} · {{ score(entry.score) }} score · {{ formatDate(entry.run.completed_at) }}</p>
              </div>
              <a class="btn-sm-outline" :href="runUrl(entry)" target="_blank" rel="noreferrer" aria-label="Run details">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </header>

            <section class="run-card-meta">
              <span>Timer: {{ duration(entry.run.clear_time_ms) }} / {{ duration(entry.run.keystone_time_ms) }}</span>
              <span>Upgrades: {{ entry.run.num_chests }}</span>
              <span>Faction: {{ entry.run.faction }}</span>
              <span>Team: {{ teamLabel(entry.run) }}</span>
            </section>

            <section class="chip-row">
              <span v-for="affix in entry.run.weekly_modifiers" :key="affix.id" class="affix-icon" :title="affix.name">
                <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
              </span>
            </section>

            <section class="roster-grid">
              <article v-for="member in entry.run.roster" :key="member.character.path" class="card member-card">
                <section class="character-line">
                  <img
                    class="icon-image"
                    :src="classIcon(member.character.class.slug)"
                    :alt="member.character.class.name"
                    loading="lazy"
                  >
                  <div>
                    <strong>{{ member.character.name }}</strong>
                    <p>{{ member.character.class.name }} · {{ member.character.spec.name }} · {{ member.role }}</p>
                    <p class="muted-line">{{ member.character.region.short_name }} / {{ member.character.realm.name }}</p>
                  </div>
                </section>

                <footer class="member-actions">
                  <a class="btn-sm-outline" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                    <i class="fa-solid fa-user"></i>
                  </a>
                  <button class="btn-sm" type="button" aria-label="Add to favorites" @click="favoriteMember(member.character)">
                    <i class="fa-solid fa-star"></i>
                  </button>
                </footer>
              </article>
            </section>
          </div>
        </section>

        <section v-if="contentMode === 'ladder' && !showInitialSkeletons && viewMode === 'table'" class="table-wrap run-table-wrap">
          <table class="table dungeon-run-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Dungeon</th>
                <th>Level</th>
                <th>Time <span>(Remaining)</span></th>
                <th>Affixes</th>
                <th>Tank</th>
                <th>Healer</th>
                <th>DPS</th>
                <th>Score</th>
                <th>Faction</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredRuns" :key="`table-${entry.run.keystone_run_id}`">
                <td class="rank-cell">{{ entry.rank }}</td>
                <td class="dungeon-cell">
                  <a :href="runUrl(entry)" target="_blank" rel="noreferrer">
                    <img v-if="dungeonIcon(entry.run.dungeon)" :src="dungeonIcon(entry.run.dungeon)" :alt="entry.run.dungeon.name" loading="lazy">
                    <span>{{ entry.run.dungeon.name }}</span>
                  </a>
                </td>
                <td>+{{ entry.run.mythic_level }}</td>
                <td class="time-cell">{{ duration(entry.run.clear_time_ms) }} <span>({{ remainingTime(entry.run) }})</span></td>
                <td>
                  <div class="affix-list">
                    <a
                      v-for="affix in entry.run.weekly_modifiers"
                      :key="affix.id"
                      :href="affixUrl(affix)"
                      :title="affix.name"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img v-if="affixIcon(affix)" :src="affixIcon(affix)" :alt="affix.name" loading="lazy">
                    </a>
                  </div>
                </td>
                <td class="role-cell">
                  <div v-for="member in rosterByRole(entry.run, 'tank')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-tank"><img :src="roleImage('tank')" alt="tank" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button
                        type="button"
                        :class="{ 'is-active': isFavoriteMember(member.character) }"
                        :aria-label="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        :title="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        @click="favoriteMember(member.character)"
                      >
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button
                        type="button"
                        :class="{ 'is-active': isCompareMember(member.character) }"
                        :aria-label="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        :title="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        @click="compareMember(member.character)"
                      >
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="role-cell">
                  <div v-for="member in rosterByRole(entry.run, 'healer')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-healer"><img :src="roleImage('healer')" alt="healer" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button
                        type="button"
                        :class="{ 'is-active': isFavoriteMember(member.character) }"
                        :aria-label="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        :title="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        @click="favoriteMember(member.character)"
                      >
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button
                        type="button"
                        :class="{ 'is-active': isCompareMember(member.character) }"
                        :aria-label="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        :title="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        @click="compareMember(member.character)"
                      >
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="role-cell dps-cell">
                  <div v-for="member in rosterByRole(entry.run, 'dps')" :key="member.character.path" class="table-player">
                    <span class="role-mark is-dps"><img :src="roleImage('dps')" alt="dps" loading="lazy"></span>
                    <a :style="{ color: classColor(member.character.class.slug) }" :href="characterUrl(member.character.path)" target="_blank" rel="noreferrer">
                      {{ characterName(member.character.name) }}
                    </a>
                    <span class="table-player-popup">
                      <button
                        type="button"
                        :class="{ 'is-active': isFavoriteMember(member.character) }"
                        :aria-label="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        :title="isFavoriteMember(member.character) ? 'Remove from favorites' : 'Add to favorites'"
                        @click="favoriteMember(member.character)"
                      >
                        <i class="fa-solid fa-heart"></i>
                      </button>
                      <button
                        type="button"
                        :class="{ 'is-active': isCompareMember(member.character) }"
                        :aria-label="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        :title="isCompareMember(member.character) ? 'Remove from compare' : 'Add to compare'"
                        @click="compareMember(member.character)"
                      >
                        <i class="fa-solid fa-scale-balanced"></i>
                      </button>
                    </span>
                  </div>
                </td>
                <td class="score-cell">{{ scoreValue(entry.score) }}</td>
                <td class="faction-cell">
                  <span :class="`faction-badge is-${entry.run.faction || 'mixed'}`">
                    <img :src="factionBanner(entry.run.faction)" :alt="entry.run.faction || 'mixed faction'" loading="lazy">
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <nav v-if="contentMode === 'ladder' && !showInitialSkeletons && filteredRuns.length" class="runs-pagination" aria-label="Runs pagination">
          <button
            class="pagination-btn"
            type="button"
            :disabled="filters.page <= 0"
            aria-label="Previous page"
            @click="setRunsPage(filters.page - 1)"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <button
            v-for="page in paginationPages"
            :key="page"
            class="pagination-btn"
            :class="{ 'is-active': filters.page === page }"
            type="button"
            @click="setRunsPage(page)"
          >
            {{ page + 1 }}
          </button>
          <button
            class="pagination-btn"
            type="button"
            aria-label="Next page"
            @click="setRunsPage(filters.page + 1)"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </nav>

        <section v-if="false" class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Dungeon</th>
                <th>Level</th>
                <th>Score</th>
                <th>Time</th>
                <th>Team</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredRuns" :key="`table-${entry.run.keystone_run_id}`">
                <td>{{ entry.rank }}</td>
                <td>
                  <div class="table-title">
                    <img v-if="dungeonImage(entry.run.dungeon)" :src="dungeonImage(entry.run.dungeon)" :alt="entry.run.dungeon.name" loading="lazy">
                    <span>{{ entry.run.dungeon.name }}</span>
                  </div>
                </td>
                <td>+{{ entry.run.mythic_level }}</td>
                <td>{{ score(entry.score) }}</td>
                <td>{{ duration(entry.run.clear_time_ms) }}</td>
                <td>{{ teamLabel(entry.run) }}</td>
                <td>
                  <a class="btn-sm-outline" :href="runUrl(entry)" target="_blank" rel="noreferrer" aria-label="Run details">
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </article>

    </div>
  </section>
</template>

<script>
import { getAffixes, getCharacterProfile, getLeaderboardCapacity, getMythicPlusRuns, getMythicPlusStaticData, getSeasonCutoffs } from '../services/raiderApi'
import { createPageCacheKey, getPageDataCache, setPageDataCache } from '../services/pageDataCache'
import { scrollToPageTop } from '../utils/pageScroll'
import { formatDate, formatDuration, formatScore } from '../utils/formatters'
import { pickDefaultSeason } from '../utils/seasons'
import allianceBanner from '../assets/images/alliance-banner.svg'
import allianceHordeBanner from '../assets/images/alliance-horde-banner.svg'
import hordeBanner from '../assets/images/horde-banner.svg'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'
import dpsRoleIcon from '../assets/images/roles/role_dps.png'
import healerRoleIcon from '../assets/images/roles/role_healer.png'
import tankRoleIcon from '../assets/images/roles/role_tank.png'

const CLASS_COLORS = {
  deathknight: '#c41e3a',
  'death-knight': '#c41e3a',
  demonhunter: '#a330c9',
  'demon-hunter': '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const CLASS_ICONS = {
  deathknight: deathKnightIcon,
  'death-knight': deathKnightIcon,
  'death knight': deathKnightIcon,
  demonhunter: demonHunterIcon,
  'demon-hunter': demonHunterIcon,
  'demon hunter': demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const DEFAULT_MYTHIC_PLUS_STATE = {
  contentMode: 'stats',
  viewMode: 'grid',
  filters: {
    expansionId: 11,
    season: '',
    region: 'world',
    dungeon: 'all',
    affixes: 'all',
    page: 0,
  },
}

const CHART_RUN_PAGE_COUNT = 10
const CHART_RUN_COMPLETION_PAGE_LIMIT = 100
const CHART_RUN_COMPLETION_BATCH_SIZE = 5
const CHART_TOP_LEVEL_ONLY_THRESHOLD = 20
const MYTHIC_PLUS_STATE_KEY = 'rio-dashboard-mythic-plus-state'

const ROLE_IMAGES = {
  tank: tankRoleIcon,
  healer: healerRoleIcon,
  dps: dpsRoleIcon,
}

const FACTION_BANNERS = {
  alliance: allianceBanner,
  horde: hordeBanner,
  mixed: allianceHordeBanner,
}

const dungeonImageContext = require.context('../assets/images/dungeons', false, /\.(jpg|jpeg|png|webp)$/)
const LOCAL_DUNGEON_IMAGES = dungeonImageContext.keys().reduce((images, key) => {
  const slug = key.replace('./', '').replace(/\.(jpg|jpeg|png|webp)$/i, '')
  images[slug] = dungeonImageContext(key)
  return images
}, {})

function loadMythicPlusState() {
  try {
    const saved = JSON.parse(localStorage.getItem(MYTHIC_PLUS_STATE_KEY) || '{}')
    const contentMode = ['stats', 'ladder'].includes(saved.contentMode) ? saved.contentMode : DEFAULT_MYTHIC_PLUS_STATE.contentMode
    const viewMode = ['grid', 'table'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_MYTHIC_PLUS_STATE.viewMode

    return {
      contentMode,
      viewMode,
      filters: { ...DEFAULT_MYTHIC_PLUS_STATE.filters },
    }
  } catch (error) {
    return {
      ...DEFAULT_MYTHIC_PLUS_STATE,
      filters: { ...DEFAULT_MYTHIC_PLUS_STATE.filters },
    }
  }
}

export default {
  name: 'MythicPlusView',
  data() {
    const savedState = loadMythicPlusState()

    return {
      loading: false,
      isFiltersOpen: false,
      closingFilters: false,
      error: '',
      seasons: [],
      dungeons: [],
      runs: [],
      chartRuns: [],
      affixes: [],
      cutoffs: null,
      capacity: null,
      selectedChartLevelFilter: null,
      loadedExpansionId: null,
      contentMode: savedState.contentMode,
      viewMode: savedState.viewMode,
      filters: savedState.filters,
      draftFilters: { ...savedState.filters },
      draftSeasons: [],
      draftDungeons: [],
      loadedDraftExpansionId: null,
      animatedCutoffs: {},
      cutoffAnimationInterval: null,
      loadSequence: 0,
      isPageActive: false,
      hasResolvedData: false,
      resolvedDataKey: '',
    }
  },
  computed: {
    showInitialSkeletons() {
      return this.loading && !this.hasResolvedData
    },
    seasonOptions() {
      return this.seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
    },
    selectedSeason() {
      return this.seasons.find((season) => season.slug === this.filters.season) || null
    },
    draftSeasonOptions() {
      return this.draftSeasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
    },
    draftSelectedSeason() {
      return this.draftSeasons.find((season) => season.slug === this.draftFilters.season) || null
    },
    visibleDungeons() {
      return this.selectedSeason?.dungeons?.length ? this.selectedSeason.dungeons : this.dungeons
    },
    draftVisibleDungeons() {
      return this.draftSelectedSeason?.dungeons?.length ? this.draftSelectedSeason.dungeons : this.draftDungeons
    },
    favoriteIds() {
      return new Set(this.$store.getters.getFavorites.flatMap((item) => this.characterKeys(item.profile || item)))
    },
    compareIds() {
      return new Set(this.$store.getters.getCompareList.flatMap((item) => this.characterKeys(item.profile || item)))
    },
    dungeonMap() {
      return this.dungeons.reduce((map, dungeon) => {
        map[dungeon.slug] = dungeon
        return map
      }, {})
    },
    expansionLabel() {
      return this.expansionName(this.filters.expansionId)
    },
    selectedDungeonLabel() {
      if (this.filters.dungeon === 'all') {
        return 'All'
      }
      return this.visibleDungeons.find((dungeon) => dungeon.slug === this.filters.dungeon)?.name || this.filters.dungeon
    },
    affixesFilterLabel() {
      const labels = {
        all: 'All',
        current: 'Current week',
        tyrannical: 'Tyrannical',
        fortified: 'Fortified',
      }
      return labels[this.filters.affixes] || this.filters.affixes
    },
    filteredRuns() {
      return this.runs
    },
    maxCompletedLevel() {
      return this.chartRuns.reduce((level, entry) => Math.max(level, Number(entry.run?.mythic_level || 0)), 0)
    },
    chartLevelOptions() {
      const levelCounts = this.chartRuns.reduce((counts, entry) => {
        const level = Number(entry.run?.mythic_level || 0)
        if (level) {
          counts[level] = (counts[level] || 0) + 1
        }
        return counts
      }, {})
      const levels = Object.entries(levelCounts)
        .map(([level, count]) => ({ level: Number(level), count }))
        .sort((left, right) => right.level - left.level)

      if (!levels.length) {
        return []
      }

      if (
        levels[0].count > CHART_TOP_LEVEL_ONLY_THRESHOLD
        && this.hasFullDungeonCoverageForLevel(levels[0].level)
      ) {
        return [levels[0].level]
      }

      return levels.slice(0, 2).map((item) => item.level)
    },
    selectedChartLevel() {
      if (this.chartLevelOptions.includes(this.selectedChartLevelFilter)) {
        return this.selectedChartLevelFilter
      }

      return this.chartLevelOptions.find((level) => this.dungeonStatsForLevel(level).length > 1)
        || this.chartLevelOptions[0]
        || 0
    },
    showDungeonChart() {
      return this.filters.dungeon === 'all' && this.maxLevelDungeonStats.length > 0
    },
    showClassChart() {
      return this.classDistributionStats.length > 0
    },
    maxLevelDungeonStats() {
      if (!this.selectedChartLevel) {
        return []
      }

      return this.dungeonStatsForLevel(this.selectedChartLevel)
    },
    classStatsTotal() {
      return this.classDistributionStats.reduce((total, item) => total + item.count, 0)
    },
    classChartMaxCount() {
      return Math.max(...this.classDistributionStats.map((item) => item.count), 1)
    },
    classDistributionStats() {
      const classes = this.chartRuns.reduce((map, entry) => {
        ;(entry.run?.roster || []).forEach((member) => {
          const character = member.character || {}
          const classInfo = character.class || {}
          const specInfo = character.spec || {}
          const slug = classInfo.slug || classInfo.name || 'unknown'
          const specSlug = specInfo.slug || specInfo.name || member.role || 'unknown'

          if (!map[slug]) {
            map[slug] = {
              slug,
              name: classInfo.name || slug,
              count: 0,
              specs: {},
              color: CLASS_COLORS[slug] || 'var(--primary)',
            }
          }

          map[slug].count += 1
          if (!map[slug].specs[specSlug]) {
            map[slug].specs[specSlug] = {
              slug: specSlug,
              name: specInfo.name || specSlug,
              count: 0,
            }
          }
          map[slug].specs[specSlug].count += 1
        })
        return map
      }, {})

      const total = Object.values(classes).reduce((sum, item) => sum + item.count, 0)
      const maxCount = Math.max(...Object.values(classes).map((item) => item.count), 1)

      return Object.values(classes)
        .map((item) => ({
          ...item,
          percent: total ? Math.round((item.count / total) * 100) : 0,
          width: Math.max(4, Math.round((item.count / maxCount) * 100)),
          specs: Object.values(item.specs)
            .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
            .slice(0, 3),
        }))
        .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    },
    chartMaxCount() {
      return Math.max(...this.maxLevelDungeonStats.map((item) => item.count), 1)
    },
    maxLevelTeamTotal() {
      return this.maxLevelDungeonStats.reduce((total, item) => total + item.count, 0)
    },
    topMythicLevel() {
      return this.filteredRuns[0]?.run?.mythic_level || '—'
    },
    affixLabel() {
      return this.affixes.length ? this.affixes.map((item) => item.name).join(', ') : '—'
    },
    cutoffEntries() {
      const cutoffs = this.cutoffs?.cutoffs || {}
      return [
        { label: 'Top 0.1%', target: this.cutoffTarget(cutoffs.p999?.all?.quantileMinValue) },
        { label: 'Top 1%', target: this.cutoffTarget(cutoffs.p990?.all?.quantileMinValue) },
        { label: 'Top 10%', target: this.cutoffTarget(cutoffs.p900?.all?.quantileMinValue) },
        { label: 'Keystone Legend', target: this.cutoffTarget(cutoffs.keystoneLegend?.score) },
      ]
    },
    animatedCutoffEntries() {
      return this.cutoffEntries.map((item) => ({
        label: item.label,
        value: this.showInitialSkeletons ? '-' : this.cutoffDisplayValue(item),
      }))
    },
    cutoffLabel() {
      return this.cutoffEntries[0]?.target !== null ? Math.round(this.cutoffEntries[0].target).toString() : '—'
    },
    capacitySummary() {
      const first = this.capacity?.entries?.[0]
      if (!first) {
        return 'No leaderboard capacity data for the selected region.'
      }
      return `Minimum listed key: +${first.mythic_level} · ${first.dungeon?.name || 'Dungeon'} · ${formatDuration(first.clear_time_ms)}`
    },
    leaderboardUrl() {
      return `https://raider.io/mythic-plus-rankings/${this.filters.season}/${this.filters.dungeon}/${this.filters.region}/leaderboards-strict`
    },
    paginationPages() {
      const currentPage = Number(this.filters.page) || 0
      const startPage = Math.max(0, currentPage - 2)
      return Array.from({ length: 5 }, (_, index) => startPage + index)
    },
  },
  methods: {
    addFavorite(character, source = 'manual') {
      this.$store.dispatch('addFavorite', { character, source })
    },
    characterId(character = {}) {
      return this.characterKeys(character)[0] || ''
    },
    // Roster entries carry region/realm either as an object with a slug or as a plain string,
    // so every read of them goes through these two.
    characterRegionSlug(character = {}) {
      return String(character.region?.slug || character.region || '')
    },
    characterRealmSlug(character = {}) {
      return String(character.realm?.slug || character.realm || '')
    },
    characterKeys(character = {}) {
      const name = String(character.name || '').toLowerCase()
      const region = this.characterRegionSlug(character).toLowerCase()
      const realm = this.characterRealmSlug(character).toLowerCase()
      const realmName = String(character.realm?.name || character.realm_name || '').toLowerCase()
      const realmAlt = String(character.realm?.altSlug || character.realm?.alt_slug || '').toLowerCase()

      return [
        `${region}:${realm}:${name}`,
        `${region}:${realmName}:${name}`,
        `${region}:${realmAlt}:${name}`,
        `${realm}:${name}`,
        `${realmName}:${name}`,
        `${realmAlt}:${name}`,
      ].filter((key, index, keys) => !key.includes('::') && !key.endsWith(':') && keys.indexOf(key) === index)
    },
    isFavoriteMember(character) {
      return this.characterKeys(character).some((key) => this.favoriteIds.has(key))
    },
    isCompareMember(character) {
      return this.characterKeys(character).some((key) => this.compareIds.has(key))
    },
    notifyCharacterToggle(character, added, listName) {
      this.$store.dispatch('notify', {
        type: added ? 'success' : 'warning',
        title: added ? 'Character added' : 'Character removed',
        description: `${this.characterName(character.name)} was ${added ? 'added to' : 'removed from'} ${listName}.`,
      })
    },
    openFilters() {
      this.syncDraftFilters()
      this.closingFilters = false
      this.isFiltersOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeFilters() {
      if (!this.isFiltersOpen || this.closingFilters) return
      this.closingFilters = true
      window.setTimeout(() => {
        this.isFiltersOpen = false
        this.closingFilters = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    applyFilters() {
      this.filters = {
        ...this.filters,
        ...this.draftFilters,
        page: 0,
      }
      this.closeFilters()
      this.loadData()
    },
    resetFilters() {
      this.filters = { ...DEFAULT_MYTHIC_PLUS_STATE.filters }
      this.draftFilters = { ...DEFAULT_MYTHIC_PLUS_STATE.filters }
      this.contentMode = DEFAULT_MYTHIC_PLUS_STATE.contentMode
      this.viewMode = DEFAULT_MYTHIC_PLUS_STATE.viewMode
      this.loadData()
    },
    syncDraftFilters() {
      this.draftFilters = { ...this.filters }
      this.ensureDraftStaticData()
    },
    expansionName(expansionId) {
      const names = {
        11: 'Midnight',
        10: 'The War Within',
        9: 'Dragonflight',
        8: 'Shadowlands',
      }
      return names[expansionId] || `Expansion ${expansionId}`
    },
    setRunsPage(page) {
      const nextPage = Math.max(0, Number(page) || 0)
      if (nextPage === this.filters.page) return
      scrollToPageTop()
      this.filters.page = nextPage
      this.loadData()
    },
    setChartLevel(level) {
      this.selectedChartLevelFilter = Number(level) || null
    },
    dungeonCoverageKey(value) {
      return String(value || '').trim().toLowerCase()
    },
    hasFullDungeonCoverageForLevel(level) {
      const requiredDungeons = this.visibleDungeons
        .map((dungeon) => this.dungeonCoverageKey(dungeon.slug || dungeon.short_name || dungeon.name))
        .filter(Boolean)

      if (!requiredDungeons.length) {
        return false
      }

      const completedDungeons = new Set(
        this.dungeonStatsForLevel(level)
          .map((dungeon) => this.dungeonCoverageKey(dungeon.slug || dungeon.name))
          .filter(Boolean)
      )

      return requiredDungeons.every((dungeon) => completedDungeons.has(dungeon))
    },
    previousChartLevelForCompletion() {
      const levels = this.chartLevelOptions
      return levels.length > 1 ? Math.min(...levels) : 0
    },
    uniqueRunEntries(entries) {
      const seen = new Set()

      return entries.filter((entry) => {
        const run = entry.run || {}
        const key = run.keystone_run_id
          || run.keystone_team_id
          || `${run.season || ''}:${run.dungeon?.slug || run.dungeon?.name || ''}:${run.mythic_level || ''}:${run.clear_time_ms || ''}:${entry.rank || ''}`

        if (!key || seen.has(key)) {
          return false
        }

        seen.add(key)
        return true
      })
    },
    async completeChartRunsForPreviousLevel(runFilters, loadedPages, options = {}) {
      if (runFilters.dungeon !== 'all') {
        return
      }

      const loadedPageSet = new Set(loadedPages)
      const targetLevel = this.previousChartLevelForCompletion()

      if (!targetLevel) {
        return
      }

      for (let page = 0; page < CHART_RUN_COMPLETION_PAGE_LIMIT; page += CHART_RUN_COMPLETION_BATCH_SIZE) {
        if (options.loadSequence !== this.loadSequence) return
        const batchPages = Array.from({ length: CHART_RUN_COMPLETION_BATCH_SIZE }, (_, index) => page + index)
          .filter((batchPage) => batchPage < CHART_RUN_COMPLETION_PAGE_LIMIT && !loadedPageSet.has(batchPage))

        if (!batchPages.length) {
          continue
        }

        batchPages.forEach((batchPage) => loadedPageSet.add(batchPage))

        const batchResults = await Promise.all(
          batchPages.map((batchPage) => {
            return getMythicPlusRuns(
              { ...runFilters, page: batchPage },
              { force: options.force, cacheContext: options.cacheContext },
            )
              .then((pageData) => ({ page: batchPage, rankings: pageData.rankings || [] }))
              .catch(() => ({ page: batchPage, rankings: [], failed: true }))
          })
        )

        if (options.loadSequence !== this.loadSequence) return
        let shouldStop = false
        for (const result of batchResults.sort((left, right) => left.page - right.page)) {
          const rankings = result.rankings || []
          if (result.failed) {
            continue
          }

          if (!rankings.length) {
            shouldStop = true
            break
          }

          const pageMaxLevel = rankings.reduce((maxLevel, entry) => {
            return Math.max(maxLevel, Number(entry.run?.mythic_level || 0))
          }, 0)

          if (pageMaxLevel < targetLevel) {
            shouldStop = true
            break
          }

          this.chartRuns = this.uniqueRunEntries([
            ...this.chartRuns,
            ...rankings,
          ])
        }

        if (typeof options.onProgress === 'function') {
          options.onProgress()
        }
        if (shouldStop) return
      }
    },
    rankTierClass(index) {
      if (index < 3) return 'is-top-3'
      if (index < 5) return 'is-top-5'
      return 'is-rest'
    },
    dungeonStatsForLevel(level) {
      if (!level) {
        return []
      }

      const dungeons = this.chartRuns
        .filter((entry) => Number(entry.run?.mythic_level || 0) === Number(level))
        .reduce((map, entry) => {
          const dungeon = entry.run.dungeon || {}
          const slug = dungeon.slug || dungeon.short_name || dungeon.name
          if (!slug) {
            return map
          }

          if (!map[slug]) {
            map[slug] = {
              slug,
              name: dungeon.name || slug,
              teams: new Set(),
            }
          }

          map[slug].teams.add(entry.run.keystone_team_id || entry.run.keystone_run_id || `${entry.rank}-${slug}`)
          return map
        }, {})

      return Object.values(dungeons)
        .map((item) => ({
          slug: item.slug,
          name: item.name,
          count: item.teams.size,
        }))
        .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
    },
    chartBarHeight(count) {
      const percent = Math.max(8, Math.round((Number(count || 0) / this.chartMaxCount) * 100))
      return `${percent}%`
    },
    chartValueBottom(count) {
      const percent = Math.max(8, Math.round((Number(count || 0) / this.chartMaxCount) * 100))
      return `calc(${percent}% + 0.35rem)`
    },
    startCutoffAnimation() {
      this.stopCutoffAnimation()
      const targets = this.cutoffEntries.reduce((map, item) => {
        map[item.label] = item.target
        return map
      }, {})
      this.animatedCutoffs = Object.fromEntries(
        Object.keys(targets).map((label) => [label, targets[label] === null ? null : 0])
      )

      const duration = 400
      const stepMs = 30
      let elapsed = 0

      this.cutoffAnimationInterval = setInterval(() => {
        elapsed += stepMs
        const progress = Math.min(elapsed / duration, 1)
        this.animatedCutoffs = Object.fromEntries(
          Object.entries(targets).map(([label, target]) => [
            label,
            target === null ? null : Math.floor(target * progress),
          ])
        )

        if (progress < 1) {
          return
        }

        this.animatedCutoffs = { ...targets }
        this.stopCutoffAnimation()
      }, stepMs)
    },
    stopCutoffAnimation() {
      if (this.cutoffAnimationInterval) {
        clearInterval(this.cutoffAnimationInterval)
        this.cutoffAnimationInterval = null
      }
    },
    cutoffDisplayValue(item) {
      const animatedValue = this.animatedCutoffs[item.label]
      if (animatedValue !== undefined) {
        return animatedValue === null ? '—' : Math.round(animatedValue).toString()
      }

      return item.target === null ? '—' : Math.round(item.target).toString()
    },
    shortDungeonName(name) {
      return String(name || '')
        .replace(/^Operation:\s*/i, 'Op. ')
        .replace(/Tazavesh:\s*/i, 'Taz. ')
        .replace(/Streets of Wonder/i, 'Streets')
        .replace(/Soleah's Gambit/i, 'Gambit')
        .replace(/Eco-Dome Al'dani/i, 'Eco-Dome')
    },
    setSeasonLabel(label) {
      this.$store.dispatch('setSeasonLabel', label)
    },
    async loadData(force = false) {
      force = force === true
      const loadSequence = ++this.loadSequence
      const initialCacheKey = createPageCacheKey('mythic-plus', this.filters)
      const cached = getPageDataCache(initialCacheKey)

      if (cached) {
        this.applyCachedPageData(cached.data)
        if (cached.isFresh && !force) {
          this.error = ''
          if (this.isPageActive) this.startCutoffAnimation()
          return
        }
      } else if (!this.hasResolvedData || this.resolvedDataKey !== initialCacheKey) {
        this.clearResolvedPageData()
      }

      this.loading = true
      this.error = ''
      this.animatedCutoffs = {}
      this.stopCutoffAnimation()
      const loadingId = await this.$store.dispatch('startGlobalLoading', { id: 'page:mythic-plus', message: 'Loading Mythic+ data...' })

      try {
        const staticCacheContext = {}
        await this.ensureStaticData(force, staticCacheContext)
        if (loadSequence !== this.loadSequence) return

        const { expansionId, ...runFilters } = this.filters
        const regionForExtras = this.filters.region === 'world' ? 'eu' : this.filters.region
        const currentPage = Number(runFilters.page || 0)
        const chartPages = Array.from({ length: CHART_RUN_PAGE_COUNT }, (_, page) => page)
        const cacheContext = {}
        const previousChartRuns = this.chartRuns
        const previousAffixes = this.affixes
        const previousCutoffs = this.cutoffs
        const previousCapacity = this.capacity
        const chartPageRequests = chartPages
          .filter((page) => page !== currentPage)
          .map((page) => getMythicPlusRuns({ ...runFilters, page }, { force, cacheContext })
            .then((pageData) => ({ ...pageData, failed: false }))
            .catch(() => ({ rankings: [], failed: true })))

        const [runsData, chartRunsData, affixesData, cutoffsData, capacityData] = await Promise.all([
          getMythicPlusRuns(runFilters, { force, cacheContext }),
          Promise.all(chartPageRequests),
          getAffixes(regionForExtras, 'ru', { force, cacheContext })
            .catch(() => ({ affix_details: previousAffixes })),
          getSeasonCutoffs(regionForExtras, this.filters.season, { force, cacheContext })
            .catch(() => previousCutoffs || {}),
          getLeaderboardCapacity(regionForExtras, '', 'current', { force, cacheContext })
            .catch(() => previousCapacity || {}),
        ])

        if (loadSequence !== this.loadSequence) return
        this.runs = runsData.rankings || []
        const chartPageFailed = chartRunsData.some((pageData) => pageData.failed)
        this.chartRuns = this.uniqueRunEntries([
          ...(chartPageFailed ? previousChartRuns : []),
          ...this.runs,
          ...chartRunsData.flatMap((pageData) => pageData.rankings || []),
        ])
        this.affixes = affixesData.affix_details || affixesData.affixes || previousAffixes || []
        this.cutoffs = cutoffsData
        this.capacity = capacityData
        if (!this.chartLevelOptions.includes(this.selectedChartLevelFilter)) {
          this.selectedChartLevelFilter = null
        }
        this.hasResolvedData = true

        const season = this.seasons.find((item) => item.slug === this.filters.season)
        this.setSeasonLabel(season ? season.short_name : 'Current Raider.io season')
        this.persistCachedPageData(initialCacheKey, cacheContext, 'partial')

        await this.completeChartRunsForPreviousLevel(
          runFilters,
          [...chartPages, currentPage],
          {
            force,
            loadSequence,
            cacheContext,
            onProgress: () => {
              if (loadSequence !== this.loadSequence) return
              this.persistCachedPageData(initialCacheKey, cacheContext, 'partial')
            },
          },
        )
        if (loadSequence !== this.loadSequence) return
        if (!this.chartLevelOptions.includes(this.selectedChartLevelFilter)) {
          this.selectedChartLevelFilter = null
        }
        this.persistCachedPageData(initialCacheKey, cacheContext, 'complete')
        if (staticCacheContext.networkRequested || cacheContext.networkRequested) {
          this.$store.dispatch('notify', {
            type: 'success',
            title: force ? 'Mythic+ data refreshed' : 'Mythic+ data updated',
            description: 'The latest runs, rankings, and seasonal data are now displayed.',
          })
        }
      } catch (error) {
        if (loadSequence !== this.loadSequence) return
        this.error = 'Unable to load Mythic+ data. Try another region or page.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Mythic+ load failed',
          description: this.error,
        })
      } finally {
        if (loadSequence === this.loadSequence) {
          this.loading = false
          if (this.isPageActive) this.startCutoffAnimation()
        }
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    persistCachedPageData(initialCacheKey, cacheContext, cacheStatus = 'partial') {
      if (!this.hasResolvedData) return

      const pageData = this.createCachedPageData(cacheStatus)
      const finalCacheKey = createPageCacheKey('mythic-plus', pageData.filters)
      const cacheOptions = { updatedAt: cacheContext.oldestUpdatedAt }
      setPageDataCache(initialCacheKey, pageData, cacheOptions)
      setPageDataCache(finalCacheKey, pageData, cacheOptions)
      this.resolvedDataKey = finalCacheKey
    },
    createCachedPageData(cacheStatus = 'complete') {
      return {
        cacheStatus,
        hasResolvedData: true,
        filters: { ...this.filters },
        seasons: this.seasons,
        dungeons: this.dungeons,
        runs: this.runs,
        chartRuns: this.chartRuns,
        affixes: this.affixes,
        cutoffs: this.cutoffs,
        capacity: this.capacity,
        loadedExpansionId: this.loadedExpansionId,
        selectedChartLevelFilter: this.selectedChartLevelFilter,
      }
    },
    applyCachedPageData(data = {}) {
      this.stopCutoffAnimation()
      this.filters = { ...this.filters, ...(data.filters || {}) }
      this.draftFilters = { ...this.filters }
      this.seasons = data.seasons || []
      this.dungeons = data.dungeons || []
      this.runs = data.runs || []
      this.chartRuns = data.chartRuns || []
      this.affixes = data.affixes || []
      this.cutoffs = data.cutoffs || null
      this.capacity = data.capacity || null
      this.loadedExpansionId = data.loadedExpansionId ?? this.filters.expansionId
      this.selectedChartLevelFilter = data.selectedChartLevelFilter || null
      this.hasResolvedData = data.hasResolvedData !== false
      this.resolvedDataKey = createPageCacheKey('mythic-plus', this.filters)
      this.animatedCutoffs = {}
      const season = this.seasons.find((item) => item.slug === this.filters.season)
      this.setSeasonLabel(season ? season.short_name : 'Current Raider.io season')
    },
    clearResolvedPageData() {
      this.runs = []
      this.chartRuns = []
      this.affixes = []
      this.cutoffs = null
      this.capacity = null
      this.selectedChartLevelFilter = null
      this.hasResolvedData = false
      this.resolvedDataKey = ''
      this.animatedCutoffs = {}
    },
    async ensureStaticData(force = false, cacheContext = null) {
      const staticData = await getMythicPlusStaticData(this.filters.expansionId, { force, cacheContext })
      this.seasons = staticData.seasons || []
      this.dungeons = staticData.dungeons || []
      this.loadedExpansionId = this.filters.expansionId
      this.normalizeFilters('filters', this.seasons, this.dungeons)
    },
    async ensureDraftStaticData() {
      if (this.loadedDraftExpansionId !== this.draftFilters.expansionId || !this.draftSeasons.length) {
        const requestedExpansionId = this.draftFilters.expansionId
        const loadingId = await this.$store.dispatch('startGlobalLoading', {
          id: 'page:mythic-plus-filters',
          message: 'Loading Mythic+ filters...',
        })
        try {
          const staticData = await getMythicPlusStaticData(requestedExpansionId)
          if (this.draftFilters.expansionId !== requestedExpansionId) return

          this.draftSeasons = staticData.seasons || []
          this.draftDungeons = staticData.dungeons || []
          this.loadedDraftExpansionId = requestedExpansionId
        } finally {
          this.$store.dispatch('finishGlobalLoading', loadingId)
        }
      }
      this.normalizeFilters('draftFilters', this.draftSeasons, this.draftDungeons)
    },
    normalizeFilters(targetKey, seasons, dungeons) {
      const target = this[targetKey]
      if (!seasons.some((season) => season.slug === target.season)) {
        target.season = pickDefaultSeason(seasons)?.slug || ''
      }

      const selectedSeason = seasons.find((season) => season.slug === target.season) || null
      const visibleDungeons = selectedSeason?.dungeons?.length ? selectedSeason.dungeons : dungeons
      if (!visibleDungeons.some((dungeon) => dungeon.slug === target.dungeon)) {
        target.dungeon = 'all'
      }
    },
    async favoriteMember(character) {
      const keys = this.characterKeys(character)
      if (this.isFavoriteMember(character)) {
        this.$store.commit('setFavorites', this.$store.getters.getFavorites.filter((item) => {
          const itemKeys = this.characterKeys(item.profile || item)
          return !itemKeys.some((key) => keys.includes(key))
        }))
        this.notifyCharacterToggle(character, false, 'favorites')
        return
      }

      const loadingId = await this.$store.dispatch('startGlobalLoading', {
        id: `mythic-plus:favorite:${keys[0] || character.name}`,
        message: `Loading ${character.name} profile...`,
      })
      try {
        const profile = await getCharacterProfile({
          region: this.characterRegionSlug(character),
          realm: this.characterRealmSlug(character),
          name: character.name,
          fields: [
            'gear',
            'guild',
            'raid_progression:current-expansion',
            'mythic_plus_scores_by_season:current',
            'mythic_plus_best_runs:all',
            'mythic_plus_recent_runs',
            'mythic_plus_ranks',
          ].join(','),
        }, { force: true })
        const normalized = {
          id: this.characterId(profile),
          source: 'mythic_plus',
          addedAt: new Date().toISOString(),
          profile,
        }
        this.$store.commit('setFavorites', [normalized, ...this.$store.getters.getFavorites.filter((item) => item.id !== normalized.id)])
        this.notifyCharacterToggle(character, true, 'favorites')
      } catch (error) {
        this.error = `Unable to add ${character.name} to favorites.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Favorite failed',
          description: this.error,
        })
      } finally {
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async compareMember(character) {
      const keys = this.characterKeys(character)
      if (this.isCompareMember(character)) {
        this.$store.commit('setCompareList', this.$store.getters.getCompareList.filter((item) => {
          const itemKeys = this.characterKeys(item.profile || item)
          return !itemKeys.some((key) => keys.includes(key))
        }))
        this.notifyCharacterToggle(character, false, 'compare')
        return
      }

      const loadingId = await this.$store.dispatch('startGlobalLoading', {
        id: `mythic-plus:compare:${keys[0] || character.name}`,
        message: `Loading ${character.name} profile...`,
      })
      try {
        const profile = await this.fetchCharacterProfile(character, { force: true })
        const normalized = {
          id: this.characterId(profile),
          source: 'mythic_plus',
          addedAt: new Date().toISOString(),
          profile,
        }
        const compareList = [...this.$store.getters.getCompareList.filter((item) => item.id !== normalized.id)]
        if (compareList.length >= 6) {
          compareList.shift()
        }
        compareList.push(normalized)
        this.$store.commit('setCompareList', compareList)
        this.notifyCharacterToggle(character, true, 'compare')
      } catch (error) {
        this.error = `Unable to add ${character.name} to compare.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Compare failed',
          description: this.error,
        })
      } finally {
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    fetchCharacterProfile(character, options = {}) {
      return getCharacterProfile({
        region: this.characterRegionSlug(character),
        realm: this.characterRealmSlug(character),
        name: character.name,
        fields: [
          'gear',
          'guild',
          'raid_progression:current-expansion',
          'mythic_plus_scores_by_season:current',
          'mythic_plus_best_runs:all',
          'mythic_plus_recent_runs',
          'mythic_plus_ranks',
        ].join(','),
      }, options)
    },
    scoreValue(value) {
      if (value === null || value === undefined) return '-'
      return Math.round(Number(value)).toString()
    },
    cutoffScore(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
      return Math.round(Number(value)).toString()
    },
    cutoffTarget(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return null
      return Math.round(Number(value))
    },
    remainingTime(run) {
      const remaining = run.time_remaining_ms ?? (run.keystone_time_ms - run.clear_time_ms)
      return formatDuration(Math.max(remaining || 0, 0))
    },
    rosterByRole(run, role) {
      return run.roster.filter((member) => member.role === role)
    },
    characterName(name) {
      return (name || '').split('-')[0]
    },
    classColor(classSlug) {
      if (this.$store.state.themeMode !== 'dark') {
        return 'var(--foreground)'
      }
      return CLASS_COLORS[classSlug] || 'var(--foreground)'
    },
    roleImage(role) {
      return ROLE_IMAGES[role] || ROLE_IMAGES.dps
    },
    factionBanner(faction) {
      return FACTION_BANNERS[faction] || FACTION_BANNERS.mixed
    },
    persistMythicPlusState() {
      try {
        localStorage.setItem(MYTHIC_PLUS_STATE_KEY, JSON.stringify({
          contentMode: this.contentMode,
          viewMode: this.viewMode,
        }))
      } catch (error) {
        console.warn('Unable to save Mythic+ state', error)
      }
    },
    affixUrl(affix) {
      return `https://www.wowhead.com/affix=${affix.id}`
    },
    score(value) { return formatScore(value) },
    duration(value) { return formatDuration(value) },
    formatDate(value) { return formatDate(value) },
    characterUrl(path) { return `https://raider.io${path}` },
    normalizeAssetUrl(url) {
      if (!url) return ''
      return url.startsWith('http') ? url : `https://cdn.raiderio.net${url}`
    },
    dungeonImage(dungeon) {
      const staticDungeon = this.dungeonMap[dungeon.slug] || dungeon
      const imageUrl = staticDungeon.background_image_url || ''
      if (imageUrl && !imageUrl.includes('/icons/')) {
        return this.normalizeAssetUrl(imageUrl)
      }

      return LOCAL_DUNGEON_IMAGES[dungeon.slug] || this.normalizeAssetUrl(staticDungeon.icon_url || dungeon.icon_url)
    },
    dungeonIcon(dungeon) {
      const staticDungeon = this.dungeonMap[dungeon.slug] || dungeon
      return this.normalizeAssetUrl(staticDungeon.icon_url || staticDungeon.background_image_url)
    },
    affixIcon(affix) {
      return affix.icon ? `https://cdn.raiderio.net/images/wow/icons/large/${affix.icon}.jpg` : ''
    },
    classIcon(classSlug) {
      return CLASS_ICONS[String(classSlug || '').toLowerCase()] || ''
    },
    runUrl(entry) { return `https://raider.io/mythic-plus-runs/${entry.run.season}/${entry.run.keystone_run_id}-${entry.run.mythic_level}-${entry.run.dungeon.slug}` },
    teamLabel(run) {
      const leadNames = run.roster.slice(0, 3).map((member) => member.character.name)
      return `#${run.keystone_team_id || run.keystone_run_id} · ${leadNames.join(', ')}`
    },
    closeFiltersOnEscape(event) {
      if (event.key === 'Escape' && this.isFiltersOpen) {
        this.closeFilters()
      }
    },
  },
  mounted() {
    this.isPageActive = true
    this.loadData()
    window.addEventListener('keydown', this.closeFiltersOnEscape)
  },
  beforeUnmount() {
    this.isPageActive = false
    this.stopCutoffAnimation()
    window.removeEventListener('keydown', this.closeFiltersOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  watch: {
    contentMode() {
      this.persistMythicPlusState()
    },
    viewMode() {
      this.persistMythicPlusState()
    },
    'draftFilters.expansionId'() {
      this.draftFilters.season = ''
      this.draftFilters.dungeon = 'all'
      this.ensureDraftStaticData()
    },
  },
}
</script>
